import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Download, Visibility, Info } from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService, { EvaluationResult } from '../services/api';
import Header from '../components/Header';
import { RENAISSANCE_COLORS } from '../utils';

const ResultsBrowser: React.FC = () => {
  const [results, setResults] = useState<EvaluationResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResult, setSelectedResult] = useState<EvaluationResult | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    loadResults();
  }, []);

  const loadResults = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.listEvaluationResults();
      if (response.status === 'success') {
        setResults(response.results || []);
      } else {
        setError('Failed to load evaluation results');
      }
    } catch (err: any) {
      console.error('Failed to load evaluation results:', err);
      setError(err.message || 'Failed to load evaluation results');
      toast.error(err.message || 'Failed to load evaluation results');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (evalRunId: string) => {
    try {
      const response = await apiService.getEvaluationMetadata(evalRunId);
      if (response.status === 'success') {
        setSelectedResult(response.metadata);
        setDetailDialogOpen(true);
      } else {
        toast.error('Failed to load evaluation details');
      }
    } catch (err: any) {
      console.error('Failed to load evaluation details:', err);
      toast.error(err.message || 'Failed to load evaluation details');
    }
  };

  const handleDownload = async (evalRunId: string) => {
    try {
      setDownloadingId(evalRunId);
      const response = await apiService.downloadEvaluationResult(evalRunId);
      if (response.status === 'success') {
        // Open download URL in new tab
        window.open(response.download_url, '_blank');
        toast.success('Download started');
      } else {
        toast.error('Failed to generate download URL');
      }
    } catch (err: any) {
      console.error('Failed to download evaluation result:', err);
      toast.error(err.message || 'Failed to download evaluation result');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div
      style={{
        backgroundColor: RENAISSANCE_COLORS.beige,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ color: 'primary.main' }}>
              Evaluation Results
            </Typography>
            <Button variant="outlined" onClick={loadResults} disabled={loading}>
              Refresh
            </Button>
          </Box>

          <Typography variant="body1" paragraph>
            Browse and download historical evaluation results stored in S3. Each evaluation run includes
            scoring results and QWK (Quadratic Weighted Kappa) scores.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : results.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No evaluation results found. Run an evaluation to see results here.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Evaluation Run ID</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Date</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Rows Processed</strong>
                    </TableCell>
                    <TableCell>
                      <strong>QWK Scores</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Size</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result) => (
                    <TableRow key={result.eval_runid} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {result.eval_runid}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(result.timestamp)}</TableCell>
                      <TableCell>{result.rows_processed.toLocaleString()}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {Object.entries(result.qwk_scores || {}).map(([trait, score]) => (
                            <Chip
                              key={trait}
                              label={`${trait}: ${score.toFixed(4)}`}
                              size="small"
                              color={score >= 0.7 ? 'success' : score >= 0.5 ? 'warning' : 'error'}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>{formatFileSize(result.size)}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(result.eval_runid)}
                              color="primary"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download Parquet File">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(result.eval_runid)}
                              disabled={downloadingId === result.eval_runid}
                              color="primary"
                            >
                              {downloadingId === result.eval_runid ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Download fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Info color="primary" />
            <Typography variant="h6">Evaluation Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedResult && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Evaluation Run ID:</strong>
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                {selectedResult.eval_runid}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                <strong>Timestamp:</strong>
              </Typography>
              <Typography variant="body2" mb={2}>
                {formatDate(selectedResult.timestamp)}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                <strong>Rows Processed:</strong>
              </Typography>
              <Typography variant="body2" mb={2}>
                {selectedResult.rows_processed.toLocaleString()}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                <strong>QWK Scores:</strong>
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {Object.entries(selectedResult.qwk_scores || {}).map(([trait, score]) => (
                  <Chip
                    key={trait}
                    label={`${trait}: ${score.toFixed(4)}`}
                    color={score >= 0.7 ? 'success' : score >= 0.5 ? 'warning' : 'error'}
                  />
                ))}
              </Box>

              {selectedResult.dataset_s3_path && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Dataset S3 Path:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                    {selectedResult.dataset_s3_path}
                  </Typography>
                </>
              )}

              <Typography variant="subtitle2" gutterBottom>
                <strong>S3 Parquet Path:</strong>
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                {selectedResult.s3_parquet_path}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                <strong>S3 QWK File Path:</strong>
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
                {selectedResult.s3_qwk_path}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                <strong>File Size:</strong>
              </Typography>
              <Typography variant="body2">{formatFileSize(selectedResult.size)}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          {selectedResult && (
            <Button
              variant="contained"
              onClick={() => {
                handleDownload(selectedResult.eval_runid);
                setDetailDialogOpen(false);
              }}
              startIcon={<Download />}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ResultsBrowser;
